import { FC } from 'react';
import { RealEstateDto } from 'api/real-estate';
import GenericTable from 'components/display/Table/Table';
import {
  formatAddress,
  formatEuroPrice,
  formatPropertyType
} from 'features/subscription-process/home-insurance/utils/realEstateFormatters';

interface BiensTableProps {
  biens: RealEstateDto[];
  isLoading?: boolean;
  onSelectBien: (bien: RealEstateDto) => void;
  /** Fonction de traduction (labels REAL_ESTATE.*) */
  t: (key: string) => string;
  pageSize?: number;
  /** Pagination serveur : si absents, pagination client sur les biens fournis. */
  currentPage?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
}

/**
 * Liste des biens immobiliers d'un client, même design que la liste de
 * sélection de bien du tunnel de souscription GLI/CBNO (RealEstateList).
 * Le clic sur une ligne ouvre le drawer de détail du bien.
 */
export const BiensTable: FC<BiensTableProps> = ({
  biens,
  isLoading = false,
  onSelectBien,
  t,
  pageSize = 10,
  currentPage,
  totalItems,
  onPageChange
}) => {
  const isServerPaginated = currentPage !== undefined && onPageChange !== undefined;
  const total = totalItems ?? biens.length;

  return (
    <GenericTable<RealEstateDto>
      config={{
        variant: 'soft',
        loading: isLoading,
        skeletonRowCount: pageSize,
        data: biens,
        onRowClick: (row) => onSelectBien(row),
        columns: [
          {
            key: 'name',
            label: t('REAL_ESTATE.LABELS.NAME'),
            sortable: true,
            render: (_value, row) => (
              <div className="flex items-center gap-3">
                <img
                  src={row.photoUrl || '/assets/images/real_estate_default.png'}
                  alt={row.name}
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                  loading="lazy"
                />
                <div className="flex flex-col min-w-0">
                  <span className="font-medium text-gray-900">{row.name}</span>
                  <span className="text-xs text-gray-500">{formatAddress(row.address)}</span>
                </div>
              </div>
            ),
          },
          {
            key: 'propertyType',
            label: t('REAL_ESTATE.LABELS.TYPE'),
            render: (_value, row) => (
              <span className="text-gray-600">{formatPropertyType(row.propertyType, t)}</span>
            ),
          },
          {
            key: 'area',
            label: t('REAL_ESTATE.LABELS.AREA'),
            align: 'center',
            render: (_value, row) => <span>{row.area} m²</span>,
          },
          {
            key: 'roomCount',
            label: 'Pièces',
            align: 'center',
          },
          {
            key: 'rentAmount',
            label: 'Loyer',
            align: 'right',
            render: (_value, row) =>
              row.rentAmount !== undefined && row.rentAmount > 0 ? (
                <span className="font-medium text-gray-900">{formatEuroPrice(row.rentAmount)}</span>
              ) : (
                <span className="text-gray-400 italic">—</span>
              ),
          },
        ],
        pagination: {
          enabled: true,
          pageSize,
          totalItems: total,
          totalPages: Math.max(1, Math.ceil(total / pageSize)),
          showInfo: true,
          ...(isServerPaginated ? { currentPage, onPageChange } : {}),
        },
      }}
    />
  );
};
